/**
 * Shared JSON merge helpers for website preview (template + published website).
 */

/** Map API page names to preview page-type slugs (single source of truth). */
export const PREVIEW_PAGE_TYPE_MAP: Record<string, string> = {
  'express-washes': 'express-wash-packages',
  'express-wash': 'express-wash-packages',
  'express-wash-packages': 'express-wash-packages',
  'fleets': 'fleet-management',
  'fleet': 'fleet-management',
  'fleet-management': 'fleet-management',
  'coupons': 'coupons',
  'coupon': 'coupons',
  'express-details': 'express-auto-details',
  'express-auto-details': 'express-auto-details',
  'home': 'home',
  'homepage': 'home',
  'memberships': 'memberships',
  'membership': 'memberships'
};

export function resolvePreviewPageType(pageName: string): string {
  const normalizedName = (pageName || '').toLowerCase().trim();
  const pageType = normalizedName.replace(/\s+/g, '-');
  if (PREVIEW_PAGE_TYPE_MAP[pageType]) {
    return PREVIEW_PAGE_TYPE_MAP[pageType];
  }
  if (PREVIEW_PAGE_TYPE_MAP[normalizedName]) {
    return PREVIEW_PAGE_TYPE_MAP[normalizedName];
  }
  return pageType || 'home';
}

export function isHomePreviewPage(pageName: string): boolean {
  const normalizedName = (pageName || '').toLowerCase().trim();
  return normalizedName === 'home' || normalizedName === 'homepage';
}

/**
 * Extract shared website config (branding, header, footer) from pages or a website record.
 */
export function extractSharedWebsiteJson(pages: any[], website?: any): any | null {
  const fromWebsite = website?.json_data?.website;
  if (fromWebsite) {
    return fromWebsite;
  }

  const homePage = pages.find((p: any) => p.isHomePage || isHomePreviewPage(p.name));
  const candidates = [homePage, ...pages].filter(Boolean);
  for (const page of candidates) {
    const websiteJson = page?.json_data?.website;
    if (websiteJson) {
      return websiteJson;
    }
  }

  const fallbackPage = homePage ?? pages[0];
  const pageJson = fallbackPage?.json_data;
  const theme = pageJson?.page?.layout?.theme;
  if (!theme) {
    return null;
  }

  return {
    branding: {
      colors: {
        primary: theme.primary_color,
        heading: theme.primary_color,
        paragraph: '#64748B'
      },
      fonts: {
        heading: theme.font_family?.split(',')?.[0]?.trim() || 'Inter',
        body: theme.font_family?.split(',')?.[0]?.trim() || 'Inter'
      }
    }
  };
}

/**
 * Merge shared website-level JSON into a page json_data payload (same as published website preview).
 */
export function mergeWebsiteJsonIntoPage(pageJson: any, sharedWebsiteJson: any | null | undefined): any {
  if (!pageJson) {
    return pageJson;
  }
  if (!sharedWebsiteJson) {
    return pageJson;
  }

  return {
    ...pageJson,
    website: {
      ...pageJson.website,
      ...sharedWebsiteJson,
      branding: {
        ...pageJson.website?.branding,
        ...sharedWebsiteJson.branding,
        colors: {
          ...pageJson.website?.branding?.colors,
          ...sharedWebsiteJson.branding?.colors
        },
        fonts: {
          ...pageJson.website?.branding?.fonts,
          ...sharedWebsiteJson.branding?.fonts
        },
        logo: {
          ...pageJson.website?.branding?.logo,
          ...sharedWebsiteJson.branding?.logo
        }
      },
      header: {
        ...pageJson.website?.header,
        ...sharedWebsiteJson.header
      },
      footer: {
        ...pageJson.website?.footer,
        ...sharedWebsiteJson.footer
      }
    }
  };
}

export function applyBrandingFromWebsiteJson(
  websiteJson: any | null | undefined,
  setters: {
    setPrimaryColor?: (value: string) => void;
    setHeadingColor?: (value: string) => void;
    setParagraphColor?: (value: string) => void;
    setHeadingFont?: (value: string) => void;
    setBodyFont?: (value: string) => void;
  }
): void {
  const branding = websiteJson?.branding;
  if (!branding) {
    return;
  }

  if (setters.setPrimaryColor && branding.colors?.primary) {
    setters.setPrimaryColor(branding.colors.primary);
  }
  if (setters.setHeadingColor && branding.colors?.heading) {
    setters.setHeadingColor(branding.colors.heading);
  }
  if (setters.setParagraphColor && branding.colors?.paragraph) {
    setters.setParagraphColor(branding.colors.paragraph);
  }
  if (setters.setHeadingFont && branding.fonts?.heading) {
    setters.setHeadingFont(branding.fonts.heading);
  }
  if (setters.setBodyFont && branding.fonts?.body) {
    setters.setBodyFont(branding.fonts.body);
  }
}

export function mapPreviewPagesFromApi(rawPages: any[]): any[] {
  return rawPages
    .filter((page: any) => (page.json_data?.page?.active ?? page.active) !== false)
    .map((page: any) => {
      const pageName = page?.name || page?.json_data?.page?.title || 'Page';
      const pageType = resolvePreviewPageType(pageName);
      return {
        id: page.id,
        name: pageName,
        type: pageType,
        active: true,
        json_data: page.json_data,
        isHomePage: isHomePreviewPage(pageName)
      };
    });
}
